/**
 * DedicatedServerDetailPage — Détail d'un serveur avec 4 tabs.
 *
 * Route : /ops/dedicated-servers/:id
 *
 * Tabs : Vue d'ensemble | Logs provisioning | Workspaces liés | Historique admin.
 * Tab synchronisée avec URL via ?tab=...
 *
 * @spec specs/app/admin/dedicated-servers.md §5
 */

import { CanAccess, useCustom, useOne } from '@refinedev/core';
import {
  Alert,
  App as AntdApp,
  Breadcrumb,
  Button,
  Descriptions,
  Empty,
  Result,
  Spin,
  Tabs,
  Timeline,
} from 'antd';
import {
  ArrowLeft,
  Clock,
  Copy,
  ExternalLink,
  Server as ServerIcon,
  Terminal,
} from 'lucide-react';
import { useMemo, useState, type FC } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import ServerActionsToolbar from './components/ServerActionsToolbar';
import ServerCapabilitiesPanel from './components/ServerCapabilitiesPanel';
import ServerFailureBanner from './components/ServerFailureBanner';
import ServerFullLogsDrawer from './components/ServerFullLogsDrawer';
import ServerMetricsLiveChart from './components/ServerMetricsLiveChart';
import ServerProvisioningLogTimeline from './components/ServerProvisioningLogTimeline';
import ServerRebuildModal from './components/ServerRebuildModal';
import ServerRetryModal from './components/ServerRetryModal';
import ServerStatusBadge from './components/ServerStatusBadge';
import ServerWorkspacesList from './components/ServerWorkspacesList';
import type {
  AdminAction,
  AdminActionLog,
  DedicatedServer,
} from '@/types/dedicatedServer';
import { REGION_LABELS, SERVER_PLAN_LABELS } from '@/types/dedicatedServer';

const TAB_KEYS = ['overview', 'provisioning-logs', 'workspaces', 'audit'] as const;
type TabKey = (typeof TAB_KEYS)[number];

const isTabKey = (value: string | null): value is TabKey =>
  TAB_KEYS.includes((value ?? '') as TabKey);

const formatAbsolute = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatRelative = (iso: string): string => {
  const date = new Date(iso);
  const diff = Date.now() - date.getTime();
  if (Number.isNaN(diff)) return '—';
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return "à l'instant";
  if (minutes < 60) return `il y a ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `il y a ${hours} h`;
  return `il y a ${Math.floor(hours / 24)} j`;
};

const ACTION_COLOR: Record<AdminAction, string> = {
  retry: 'blue',
  rebuild: 'orange',
  destroy: 'red',
  suspend: 'gold',
  resume: 'green',
  provision: 'purple',
  force_cancel: 'volcano',
};

const ACTION_LABEL: Record<AdminAction, string> = {
  retry: 'Retry',
  rebuild: 'Rebuild',
  destroy: 'Destroy',
  suspend: 'Suspend',
  resume: 'Resume',
  provision: 'Provision',
  force_cancel: 'Force cancel',
};

const DedicatedServerDetailPage: FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { message } = AntdApp.useApp();

  const tabParam = searchParams.get('tab');
  const activeTab: TabKey = isTabKey(tabParam) ? tabParam : 'overview';

  const [fullLogsOpen, setFullLogsOpen] = useState(false);
  const [rebuildOpen, setRebuildOpen] = useState(false);
  const [retryOpen, setRetryOpen] = useState(false);

  const { data, isLoading, isError, refetch } = useOne<DedicatedServer>({
    resource: 'admin/dedicated-servers',
    id: id ?? '',
    queryOptions: { enabled: !!id, retry: 0 },
  });

  const server = data?.data;

  const handleTabChange = (key: string) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set('tab', key);
      return params;
    });
  };

  const handleCopy = (value: string, label: string) => {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      void navigator.clipboard.writeText(value).then(() => {
        void message.success(`${label} copié`);
      });
    }
  };

  const handleDownloadLogs = async () => {
    if (!server) return;
    try {
      const base = (import.meta.env.VITE_API_URL as string | undefined) ?? '';
      const res = await fetch(
        `${base}/api/v1/admin/dedicated-servers/${server.id}/full-logs`,
        { credentials: 'include' },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const text = await res.text();
      const blob = new Blob([text], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${server.name}-logs.txt`;
      a.click();
      URL.revokeObjectURL(url);
      void message.success('Logs téléchargés');
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      void message.error(`Téléchargement échoué : ${msg}`);
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spin size="large" />
      </div>
    );
  }

  if (isError || !server) {
    return (
      <Result
        status="error"
        title="Serveur introuvable"
        subTitle="Le serveur demandé n'existe pas ou vous n'avez pas les droits nécessaires."
        extra={
          <Button onClick={() => navigate('/ops/dedicated-servers')}>Retour à la liste</Button>
        }
      />
    );
  }

  const regionCfg = REGION_LABELS[server.region];

  return (
    <CanAccess
      resource="admin/dedicated-servers"
      action="show"
      fallback={<Result status="403" title="Accès refusé" />}
    >
      <div className="space-y-4">
        {/* Breadcrumb + back */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button
              icon={<ArrowLeft className="h-4 w-4" />}
              onClick={() => navigate(-1)}
              size="small"
            >
              Retour
            </Button>
            <Breadcrumb
              items={[
                { title: 'Ops' },
                {
                  title: (
                    <a
                      onClick={(e) => {
                        e.preventDefault();
                        navigate('/ops/dedicated-servers');
                      }}
                      href="/ops/dedicated-servers"
                    >
                      Serveurs dédiés
                    </a>
                  ),
                },
                { title: server.name },
              ]}
            />
          </div>
          <Button
            size="small"
            icon={<Copy className="h-3.5 w-3.5" />}
            onClick={() => handleCopy(server.id, 'ID')}
          >
            Copier ID
          </Button>
        </div>

        {/* Header sticky */}
        <div className="fc-card sticky top-2 z-10 p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <ServerIcon className="h-6 w-6 text-slate-400" />
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-lg font-bold text-slate-900 dark:text-white">
                    {server.name}
                  </h1>
                  <ServerStatusBadge status={server.status} size="large" />
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {server.organization_name ?? server.organization_id.slice(0, 8)} · Plan{' '}
                  {SERVER_PLAN_LABELS[server.plan] ?? server.plan} ·{' '}
                  {regionCfg?.flag ?? '🌍'} {regionCfg?.label ?? server.region} ·{' '}
                  {server.specs.cpu} CPU · {server.specs.ram_gb} Go · {server.specs.disk_gb} Go
                </p>
              </div>
            </div>
            <ServerActionsToolbar
              server={server}
              onFullLogsClick={() => setFullLogsOpen(true)}
              onActionSuccess={() => void refetch()}
            />
          </div>
        </div>

        {/* Tabs */}
        <Tabs
          activeKey={activeTab}
          onChange={handleTabChange}
          items={[
            {
              key: 'overview',
              label: "Vue d'ensemble",
              children: (
                <OverviewTab
                  server={server}
                  onRetryClick={() => setRetryOpen(true)}
                  onRebuildClick={() => setRebuildOpen(true)}
                  onFullLogsClick={() => setFullLogsOpen(true)}
                  onCopy={handleCopy}
                />
              ),
            },
            {
              key: 'provisioning-logs',
              label: 'Logs de provisioning',
              children: (
                <ServerProvisioningLogTimeline
                  serverId={server.id}
                  serverName={server.name}
                  serverStatus={server.status}
                  onFullLogsClick={() => setFullLogsOpen(true)}
                  onDownloadClick={() => void handleDownloadLogs()}
                />
              ),
            },
            {
              key: 'workspaces',
              label: 'Workspaces liés',
              children: <ServerWorkspacesList server={server} />,
            },
            {
              key: 'audit',
              label: "Historique d'actions",
              children: <AuditTab serverId={server.id} />,
            },
          ]}
        />

        {/* Drawers / modales globales */}
        <ServerFullLogsDrawer
          serverId={server.id}
          serverName={server.name}
          open={fullLogsOpen}
          onClose={() => setFullLogsOpen(false)}
        />
        <ServerRetryModal
          server={server}
          open={retryOpen}
          onClose={() => setRetryOpen(false)}
          onSuccess={() => void refetch()}
        />
        <ServerRebuildModal
          server={server}
          open={rebuildOpen}
          onClose={() => setRebuildOpen(false)}
          onSuccess={() => void refetch()}
        />
      </div>
    </CanAccess>
  );
};

// ============================================================================
// TABS
// ============================================================================

interface OverviewTabProps {
  server: DedicatedServer;
  onRetryClick: () => void;
  onRebuildClick: () => void;
  onFullLogsClick: () => void;
  onCopy: (value: string, label: string) => void;
}

const OverviewTab: FC<OverviewTabProps> = ({
  server,
  onRetryClick,
  onRebuildClick,
  onFullLogsClick,
  onCopy,
}) => {
  const regionCfg = REGION_LABELS[server.region];
  const stripeBase = import.meta.env.DEV
    ? 'https://dashboard.stripe.com/test/subscriptions'
    : 'https://dashboard.stripe.com/subscriptions';

  return (
    <div className="space-y-4">
      {server.status === 'failed' && (
        <ServerFailureBanner
          server={server}
          onRetryClick={onRetryClick}
          onRebuildClick={onRebuildClick}
          onFullLogsClick={onFullLogsClick}
        />
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="fc-card p-5">
          <h3 className="mb-3 text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">
            Infos
          </h3>
          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label="Nom">{server.name}</Descriptions.Item>
            <Descriptions.Item label="ID">
              <div className="flex items-center gap-1">
                <span className="font-mono text-xs">{server.id}</span>
                <button
                  type="button"
                  onClick={() => onCopy(server.id, 'ID')}
                  className="text-slate-400 hover:text-blue-500"
                >
                  <Copy className="h-3 w-3" />
                </button>
              </div>
            </Descriptions.Item>
            <Descriptions.Item label="Organisation">
              <a
                href={`/ops/organizations/${server.organization_id}`}
                className="text-blue-600 hover:underline dark:text-blue-400"
              >
                {server.organization_name ?? server.organization_id.slice(0, 8)}
              </a>
            </Descriptions.Item>
            <Descriptions.Item label="Plan">
              {SERVER_PLAN_LABELS[server.plan] ?? server.plan}
            </Descriptions.Item>
            <Descriptions.Item label="Région">
              {regionCfg?.flag ?? '🌍'} {regionCfg?.label ?? server.region}
            </Descriptions.Item>
            <Descriptions.Item label="IP">
              {server.ip_address ? (
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs">{server.ip_address}</span>
                  <button
                    type="button"
                    onClick={() => onCopy(server.ip_address as string, 'IP')}
                    className="text-slate-400 hover:text-blue-500"
                  >
                    <Copy className="h-3 w-3" />
                  </button>
                  <button
                    type="button"
                    onClick={() => onCopy(`ssh root@${server.ip_address}`, 'SSH')}
                    className="text-slate-400 hover:text-blue-500"
                    title="Copier ssh root@{ip}"
                  >
                    <Terminal className="h-3 w-3" />
                  </button>
                </div>
              ) : (
                <span className="text-slate-400">—</span>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Hetzner Server ID">
              {server.hetzner_server_id ? (
                <a
                  href={`https://console.hetzner.cloud/projects/all/servers/${server.hetzner_server_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-blue-600 hover:underline dark:text-blue-400"
                >
                  {server.hetzner_server_id}
                  <ExternalLink className="h-3 w-3" />
                </a>
              ) : (
                <span className="text-slate-400">—</span>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Hetzner Server Name">
              {server.hetzner_server_name ?? <span className="text-slate-400">—</span>}
            </Descriptions.Item>
            <Descriptions.Item label="Créé">{formatAbsolute(server.created_at)}</Descriptions.Item>
            <Descriptions.Item label="Dernière activité">
              {formatRelative(server.last_activity_at)}
            </Descriptions.Item>
            <Descriptions.Item label="Coût mensuel">
              {server.monthly_cost_eur.toLocaleString('fr-FR')} €
            </Descriptions.Item>
            {server.stripe_subscription_id && (
              <Descriptions.Item label="Stripe Subscription">
                <a
                  href={`${stripeBase}/${server.stripe_subscription_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 font-mono text-xs text-blue-600 hover:underline dark:text-blue-400"
                >
                  {server.stripe_subscription_id}
                  <ExternalLink className="h-3 w-3" />
                </a>
              </Descriptions.Item>
            )}
          </Descriptions>
        </div>

        <ServerMetricsLiveChart
          serverId={server.id}
          serverName={server.name}
          enabled={server.status === 'active'}
        />
      </div>

      <ServerCapabilitiesPanel
        capabilities={server.capabilities ?? null}
        onRebuildClick={onRebuildClick}
      />
    </div>
  );
};

const AuditTab: FC<{ serverId: string }> = ({ serverId }) => {
  const { data, isLoading, isError } = useCustom<
    { data: AdminActionLog[] } | AdminActionLog[]
  >({
    url: `/api/v1/admin/dedicated-servers/${serverId}/audit`,
    method: 'get',
    queryOptions: { retry: 0 },
  });

  const items = useMemo<AdminActionLog[]>(() => {
    const payload = data?.data as { data?: AdminActionLog[] } | AdminActionLog[] | undefined;
    if (!payload) return [];
    if (Array.isArray(payload)) return payload;
    return payload.data ?? [];
  }, [data]);

  if (isLoading) {
    return (
      <div className="flex h-32 items-center justify-center">
        <Spin />
      </div>
    );
  }

  if (isError) {
    return (
      <Alert
        type="warning"
        showIcon
        message="Historique indisponible"
        description="L'endpoint admin GET /api/v1/admin/dedicated-servers/:id/audit n'a pas répondu. Cette vue nécessite la table admin_action_log (cf spec §8.3 et §15 Q4)."
      />
    );
  }

  if (items.length === 0) {
    return <Empty description="Aucune action admin enregistrée pour ce serveur." />;
  }

  return (
    <div className="fc-card p-5">
      <Timeline
        items={items.map((log) => ({
          color: ACTION_COLOR[log.action] ?? 'blue',
          dot: <Clock className="h-4 w-4" />,
          children: (
            <div className="space-y-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-semibold text-slate-900 dark:text-white">
                  {log.actor.name ?? log.actor.email}
                </span>
                <span className="text-[11px] text-slate-500 dark:text-slate-400">
                  {log.actor.email}
                </span>
                <span
                  className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
                    log.result === 'success'
                      ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300'
                      : 'bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300'
                  }`}
                >
                  {ACTION_LABEL[log.action] ?? log.action} · {log.result}
                </span>
                <span
                  className="text-[11px] text-slate-400"
                  title={formatAbsolute(log.occurred_at)}
                >
                  {formatRelative(log.occurred_at)}
                </span>
              </div>
              {log.reason && (
                <p className="text-xs text-slate-600 dark:text-slate-300">{log.reason}</p>
              )}
              {log.target_stage && (
                <p className="text-[11px] text-slate-500 dark:text-slate-400">
                  Stage visé : <code>{log.target_stage}</code>
                </p>
              )}
              {log.result === 'failure' && log.error_message && (
                <pre className="mt-1 max-h-20 overflow-auto rounded border border-red-200 bg-red-50 p-1.5 font-mono text-[11px] text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200">
                  {log.error_message}
                </pre>
              )}
            </div>
          ),
        }))}
      />
    </div>
  );
};

export default DedicatedServerDetailPage;
